import { useState, useCallback } from 'react'
import { api, ApiError } from '../utils/api'
import logger from '../utils/logger'

/**
 * Custom hook for managing poll data fetching and state
 * @param {string} pollId - The poll ID to fetch
 * @returns {Object} Hook state and functions
 */
function usePollData(pollId) {
  const [poll, setPoll] = useState(null)
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchPoll = useCallback(async () => {
    if (!pollId) return

    setLoading(true)
    setError('')

    try {
      logger.info('Fetching poll data', { pollId })

      const response = await api.get(`/api/polls/${pollId}`)

      setPoll(response.poll)
      setChoices(response.choices)

      logger.info('Poll data fetched successfully', {
        pollId,
        choiceCount: response.choices.length,
        totalVotes: response.choices.reduce((sum, choice) => sum + choice.vote_count, 0)
      })

    } catch (err) {
      const errorMessage = err instanceof ApiError && err.status === 404
        ? 'Poll not found or expired'
        : 'Failed to load poll. Please try again.'

      logger.error('Failed to fetch poll', { pollId, error: errorMessage })
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [pollId])

  return {
    poll,
    setPoll, // Allow external updates to poll (for edit mode)
    choices,
    loading,
    error,
    fetchPoll,
    setChoices, // Allow external updates to choices (for realtime updates)
    setError
  }
}

export default usePollData
